const fieldsToClear = [
  "appATrainingName",
  "appATrainingCert",
  "appATrainingStartDate",
  "appATrainingEndDate",
  "appATrainingYearAwd",
];

export default {
  name: "Appendix A.C - Foreign Worker Training Qualifications",
  pageInfo: {
    text: "<b><u>Note:</u></b> This section is required if training, a certification(s), and/or a license(s) is required for the job opportunity. Identify any relevant <u>completed</u> training programs, coursework, and/or other training experience (other than employment) that qualify the foreign worker for the job opportunity for which the employer is seeking permanent labor certification. This may include, but is not limited to, programs, coursework, training experience (other than employment), etc. List the most recent training completed first. Be sure to complete every field for each set. This section will collect three (3) sets of information related to training, certifications, and/or licensure. For electronic filings, if the employer needs to disclose more sections of training, certification, and/or licensure information, the filing system will automatically provide the employer with the option of adding more sets until all required responses are completed. For mailed or paper filings, the employer will make one or more copies of training, certification, and/or licensure information sets C.a through C.c under this section of the Appendix to complete and attach to the Form ETA-9089.<b><i> Submission of additional training qualifications in any other form or format will not be accepted.</i></b>",
    coloring: "grey",
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        isVisible: {
          when: "appATrainingInstName",
          is: ["N/A", "n/a", "n/A", "N/a"],
          operator: "compareFieldValuesModal",
          modal: "fwTrainingAppC",
        },
        title: "Section Complete",
        description:
          "You marked N/A, indicating that the foreign worker does not have the relevant U.S. training or foreign equivalent. To add a training requirement, please update 'N/A' to the actual U.S. training or foreign equivalent.",
        type: "info",
      },
    ],
  },
  sections: [
    {
      code: "Appendix A.C",
      machine_name: "fwTrainingAppC",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Foreign Worker Training",
      modalHeader: "Add Foreign Worker Training",
      sectionHeader: "Foreign Worker Training",
      listType: "table",
      hide: (formApi) => {
        const fieldArray = formApi.getState().values.fwTrainingAppC;
        for (var i in fieldArray) {
          const data = fieldArray[i]?.appATrainingInstName;
          if (
            data === "N/A" ||
            data === "n/a" ||
            data === "n/A" ||
            data === "N/a"
          )
            return true;
        }
      },
      validate: [
        {
          tableRequiredIf: "appendixACRequired",
          message: "Please enter in at least one row.",
          needsFieldValues: true,
          extraData: {
            valueOf: true,
          },
        },
      ],
      editButton: true,
      removeLink: "Remove Foreign Worker Training",
      modalFields: [
        {
          code: "1",
          machine_name: "appATrainingInstName",
          label: "Name of Institution/School/Training provider",
          widget: "text",
          tableHeader: "Institution",
          validate: ["required"],
          maxLength: 120,
          parent_machine_name: "add_values_field",
          clearSchemaIfNA: fieldsToClear,
          tooltip:
            "1.	Enter the full name of the institution, school, or training provider that conducted the foreign worker’s training.  If the foreign worker has completed no training, enter “N/A.”",
        },
        {
          code: "1.a",
          machine_name: "appATrainingName",
          label: "Name of training, coursework, experience received",
          tableHeader: "Name of Training",
          widget: "text",
          validate: ["required"],
          isVisible: {
            when: "appATrainingInstName",
            is: "useless",
            operator: "has_value_and_not_na",
          },
          maxLength: 120,
          tooltip: `Enter the name of training, coursework, and/or experience received by the 
          foreign worker with the institution, school, or training provider listed in question 1. 
          If the foreign worker has completed no training, enter “N/A.”`,
        },
        {
          code: "1.b",
          machine_name: "appATrainingCert",
          label: "Training/certifications/licenses attained (if applicable)",
          tableHeader: "Certification(s)",
          widget: "text",
          validate: ["required"],
          maxLength: 120,
          isVisible: {
            when: "appATrainingInstName",
            is: "useless",
            operator: "has_value_and_not_na",
          },
          tooltip:
            "Enter any license(s), certificate(s), and certification(s) attained or issued as a result of training, coursework, or experienced received by the foreign worker. If the foreign worker has completed no training, enter “N/A.”",
        },
        {
          code: "1.c",
          machine_name: "appATrainingStartDate",
          label: "Start date of training",
          tableHeader: "Start Date",
          widget: "text",
          maxLength: 7,
          parse: "monthYear",
          validate: [
            {
              monthYear: "appATrainingStartDate",
              message: "mm/yyyy format required",
            },
            {
              monthYearStartEnd: "appATrainingEndDate",
              message: "Start Date must occur before End Date.",
              needsFieldValues: true,
              extraData: {
                isInlineModal: true,
                type: "start",
              },
            },
            {
              validMonth: "appATrainingStartDate",
              message: "a valid month is required",
            },
            "required",
            "noFutureDateTextFields",
          ],
          isVisible: {
            when: "appATrainingInstName",
            is: "useless",
            operator: "has_value_and_not_na",
          },
          tooltip:
            "Enter the month and year the foreign worker began the training, coursework, or experience identified in question 1b. Use a MM/YYYY format. If the foreign worker has completed no training, leave blank.",
        },
        {
          code: "1.d",
          machine_name: "appATrainingEndDate",
          label: "End date of training",
          tableHeader: "End Date",
          widget: "text",
          maxLength: 7,
          parse: "monthYear",
          validate: [
            {
              monthYear: "appATrainingStartDate",
              message: "mm/yyyy format required",
            },
            {
              monthYearStartEnd: "appATrainingStartDate",
              message: "End Date must occur after Start Date.",
              needsFieldValues: true,
              extraData: {
                isInlineModal: true,
                type: "end",
              },
            },
            {
              validMonth: "appATrainingEndDate",
              message: "a valid month is required",
            },
            "required",
            "noFutureDateTextFields",
          ],
          isVisible: {
            when: "appATrainingInstName",
            is: "useless",
            operator: "has_value_and_not_na",
          },
          tooltip:
            "Enter the month and year the foreign worker ended the training, coursework, or experience identified in question 1b.  Use a MM/YYYY format.  If the foreign worker has completed no training, leave blank.",
        },
        {
          code: "1.e",
          machine_name: "appATrainingYearAwd",
          label: "Month/year awarded",
          tableHeader: "Awarded Date",
          widget: "text",
          maxLength: 7,
          parse: "monthYear",
          validate: [
            {
              monthYear: "appATrainingStartDate",
              message: "mm/yyyy format required",
            },
            {
              monthYearStartEnd: "appATrainingEndDate",
              message: "Cannot be before Start Date or End Date",
              needsFieldValues: true,
              extraData: {
                isInlineModal: true,
                type: "end",
              },
            },
            {
              monthYearStartEnd: "appATrainingStartDate",
              message: "Cannot be before Start Date or End Date",
              needsFieldValues: true,
              extraData: {
                isInlineModal: true,
                type: "end",
              },
            },
            {
              validMonth: "appATrainingYearAwd",
              message: "a valid month is required",
            },
            "required",
            "noFutureDateTextFields",
          ],
          isVisible: {
            when: "appATrainingInstName",
            is: "useless",
            operator: "has_value_and_not_na",
          },
          tooltip:
            "Enter the month and year the foreign worker received the license(s), certificate(s), and/or certification(s) as a result of completing the training, coursework, and/or experience identified in question 1b.  Use a MM/YYYY format.  If the foreign worker has completed no training, leave blank.",
        },
      ],
    },
  ],
};
